import type { Market } from '@backmarket/http-api/src/api-models/Market'
import type { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import type { Features } from '@backmarket/nuxt-module-marketplace/features'
import { toPosixLocale } from '@backmarket/utils/string/toPosixLocale'

import type { ContextData, TrackingConfig } from '../types'

export function getInitialContext(
  market: Market,
  features: Features,
  experiments: ReturnType<typeof useExperiments>,
  featureFlags: Record<string, unknown>,
  config: TrackingConfig,
  visitorId: string,
  sessionId: string,
) {
  const currentLocale = useI18nLocale()

  const context: ContextData = {
    // Experiments
    abtest: experiments,

    // Feature flags
    flags: featureFlags,

    // Market config
    currencyCode: market.defaultCurrency,
    // // Using "es_US" format to maintain backward compatibility with analytics events
    locale: toPosixLocale(currentLocale),
    country: market.countryCode,
    isCountryRollout: market.status === 'OPEN',

    // Module config
    service: config.service,
    release: config.version,

    // Identification
    visitorId,
    sessionId,
  }

  const hasProp = <T extends object, K extends string>(
    obj: T,
    prop: K,
  ): obj is T & Record<K, unknown> => {
    return prop in obj
  }

  // Features
  if (
    hasProp(features, 'trustmark') &&
    features.trustmark !== undefined &&
    typeof features.trustmark === 'string'
  )
    context.trustmark = features.trustmark

  if (hasProp(features, 'showCarrier') && features.showCarrier !== undefined)
    context.hasCarrierFilters = Boolean(features.showCarrier)

  if (hasProp(features, 'vatIncluded') && features.vatIncluded !== undefined)
    context.hasVATIncluded = Boolean(features.vatIncluded)

  if (
    hasProp(features, 'buyback') &&
    features.buyback !== undefined &&
    features.buyback !== null &&
    typeof features.buyback === 'object' &&
    'headerButtonEnabled' in features.buyback
  )
    context.hasBuyBack = Boolean(features.buyback?.headerButtonEnabled)

  return context
}
